<template>
  <div>
    <section-page
      title="Follow Up Sequences"
      full-height
    >
      <template v-slot:right>
        <filter-link
          v-if="hasFollowUpSequenceFeature"
          link-name="store"
          :link-params="{tab: 'followUps'}"
        >Get follow ups from Store
        </filter-link>

        <filter-button-add
          v-if="hasFollowUpSequenceFeature"
          @click="createFollowUp"
        >New Follow Up
        </filter-button-add>
      </template>

      <template v-if="hasFollowUpSequenceFeature">
        <app-loading-section
          :loading="loading"
          :count="followUpTemplates.length"
          heading="You have no follow up sequences yet"
        >
          <table-default
            :table-data="followUpTemplates"
            default-sort="label"
          >
            <b-table-column label="Name" field="label" sortable v-slot="props">
              {{ props.row.label }}
            </b-table-column>

            <b-table-column label="Description" field="description" sortable v-slot="props">
              {{ props.row.description }}
            </b-table-column>

            <b-table-column label="When Complete" field="end_step_option" sortable v-slot="props">
              {{ props.row.end_step_option | newEnumLabel(ENUMS.FollowUpSequenceTemplateEndStepOptionsEnum) }}
            </b-table-column>

            <b-table-column label="Days" field="days" width="80" numeric sortable v-slot="props">
              {{ props.row.days }}
            </b-table-column>

            <b-table-column label="Steps" field="step_count" width="80" numeric sortable v-slot="props">
              {{ props.row.step_count }} / {{ maxSteps }}
            </b-table-column>

            <b-table-column label="Leads" field="record_count" width="80" numeric sortable v-slot="props">
              {{ props.row.record_count }} / {{ maxRecords }}
            </b-table-column>

            <b-table-column cell-class="action_cell" width="280" v-slot="props">
              <button-link @click="editFollowUp(props.row)">Manage</button-link>
              <button-link :loading="isDuplicating" @click="duplicate(props.row.id)">Duplicate</button-link>
              <button-link v-if="props.row.record_count" @click="viewRecords(props.row)">View Leads</button-link>
            </b-table-column>
          </table-default>
        </app-loading-section>
      </template>

      <div v-else class="upgrade_message">
        <p class="upgrade_message--heading">Congratulations!</p>
        <p class="upgrade_message--content">You're ready to take the next step and start closing more deals with less
          effort!</p>
        <p class="upgrade_message--content">Cross channel, automated follow up sequences from ForeFront are always
          working
          for you!  It's time to let the system do the heavy lifting when it comes to nurturing your leads.  Automated drip sequences can transform your business without spending more on marketing.</p>
        <p class="upgrade_message--content"><a @click="openBillingPortal">Upgrade your account today</a> and gain access
          to
          the complete automated follow up sequences that you can copy & paste into your business today!</p>

        <div class="flex flex-centered">
          <button-outlined
            class="upgrade_message--button"
            @click="openBillingPortal"
            size="">Upgrade Your Account Now</button-outlined>
        </div>
      </div>
    </section-page>

    <modal-before-container v-if="hasFollowUpSequenceFeature">
      <modal-default
        ref="recordsModal"
        title="Leads"
        :width="1200"
      >
        <template v-slot:default>
          <app-loading-section
            :count="recordPreviews.length"
            :loading="loadingRecordPreviews"
            heading="There are no leads in this Follow Up Sequence."
          >
            <p class="mb-50 mx-50">Leads that are currently in a Follow up sequence</p>
            <table-default
              :table-data="recordPreviews"
              default-sort="label"
            >
              <b-table-column label="Property" field="property_line_1" width="150" sortable v-slot="props">
                {{ props.row.property_line_1 }}
              </b-table-column>

              <b-table-column label="Primary Contact" field="contact_full_name" width="150" v-slot="props">
                {{ props.row.contact_full_name || '--' }}
              </b-table-column>

              <b-table-column cell-class="action_cell" width="120" v-slot="props">
                <button-link @click="openRecordModal(props.row.record_id)">Manage</button-link>
              </b-table-column>
            </table-default>
          </app-loading-section>
        </template>
      </modal-default>
    </modal-before-container>

    <modal-container v-if="hasFollowUpSequenceFeature">
      <modal-crud-form
        ref="createFollowUpModal"
        title="Follow Up Sequence"
        form-component="follow-up-template-form"
        @created="editFollowUp($event)"
        @close="loadFusTemplates"
      ></modal-crud-form>

      <modal-form-edit
        ref="updateFollowUpModal"
        title="Follow Up Sequence"
        form-component="follow-up-template-form"
        @close="loadFusTemplates"
      >
        <section-page
          title="Steps"
          :description="stepsMessage"
        >
          <template v-slot:right>
            <filter-button-add v-show="!propagatingChanges" @click="createFollowUpStep">
              New Step
            </filter-button-add>
            <filter-button-add v-show="propagatingChanges" disabled>
              Propagating Changes
            </filter-button-add>
          </template>

          <app-loading-section
            :count="steps.length"
            :loading="loadingSteps"
            heading="Add Steps!!"
          >
            <table-default
              :table-data="steps"
              default-sort="day_offset"
            >
              <b-table-column label="Type" field="type" width="100" sortable v-slot="props">
                {{ props.row.type | newEnumLabel(ENUMS.FollowUpSequenceStepTypeEnum) }}
              </b-table-column>

              <b-table-column label="Preview" width="200" v-slot="props">
                <template v-if="props.row.type === ENUMS.FollowUpSequenceStepTypeEnum.Text.value">
                  {{ $utils.truncateString($utils.stripTags(props.row.body), 125) }}
                </template>
                <template v-if="props.row.type === ENUMS.FollowUpSequenceStepTypeEnum.Email.value">
                  {{ $utils.truncateString(props.row.subject.replace(/(<([^>]+)>)/gi, ""), 70) }}
                </template>
                <template v-if="props.row.type === ENUMS.FollowUpSequenceStepTypeEnum.Task.value">
                  {{ props.row.label }}
                </template>
                <template v-if="props.row.type === ENUMS.FollowUpSequenceStepTypeEnum.SendWebhook.value">
                  {{ props.row.webhook_url }}
                </template>
              </b-table-column>

              <b-table-column label="Assigned To" field="assign_to_id" sortable v-slot="props">
                {{ props.row.assign_to_id | userDisplayName }}
              </b-table-column>

              <b-table-column label="Day" field="day_offset" numeric sortable v-slot="props">
                {{ props.row.day_offset }}
              </b-table-column>

              <b-table-column label="Time" field="time" sortable numeric v-slot="props">
                {{ formatTime(props.row.time) }}
              </b-table-column>

              <b-table-column cell-class="action_cell" width="120" v-slot="props">
                <button-link v-show="propagatingChanges" :disabled="propagatingChanges">Propagating Changes
                </button-link>
                <button-link v-show="!propagatingChanges" @click="editFollowUpStep(props.row)">Manage</button-link>
              </b-table-column>
            </table-default>
          </app-loading-section>

        </section-page>
      </modal-form-edit>

      <modal-crud-form
        ref="followUpStepModal"
        title="Follow Up Sequence Step"
        form-component="follow-up-sequence-step-template-form"
        :collection.sync="steps"
        @close="loadFusTemplates"
      ></modal-crud-form>
    </modal-container>
  </div>
</template>

<script>
import {ENUMS} from "../../services/enums";
import api from "../../api";
import dates from "../../services/dates";
import router from "../../router";

export default {
  name: "FollowUpSequenceTab",
  data() {
    return {
      loading: true,
      followUpTemplates: [],

      isDuplicating: false,
      loadingSteps: true,
      steps: [],

      loadingRecordPreviews: true,
      recordPreviews: [],

      currentFollowUpId: undefined,
    };
  },
  mounted() {
    this.loadFusTemplates();
  },
  computed: {
    hasFollowUpSequenceFeature() {
      return this.$store.state.accounts.features.FollowUpSequence;
    },
    currentFollowUp() {
      if (!this.currentFollowUpId) {
        return undefined;
      }

      return this.$store.getters.fusTemplateById(this.currentFollowUpId);
    },
    propagatingChanges() {
      if (!this.currentFollowUp) {
        return false;
      }

      return this.currentFollowUp.status === ENUMS.FollowUpSequenceTemplateStatusEnum.Processing.value;
    },
    stepsMessage() {
      let message = 'Changes to steps will take 5 minutes to process.';

      if (this.currentFollowUp && this.currentFollowUp.queued_at) {
        return `Changes will get propagated to all existing Leads in the Follow Up Sequence at ${dates.formatAsTime(this.currentFollowUp.queued_at)}`;
      }

      return message;
    },
    maxSteps() {
      return process.env.MIX_FOLLOW_UP_SEQUENCE_MAX_STEPS_PER_TEMPLATE;
    },
    maxRecords() {
      return process.env.MIX_FOLLOW_UP_SEQUENCE_MAX_RECORDS_PER_TEMPLATES;
    }
  },
  methods: {
    formatTime(timestamp) {
      // Have to add the user timezone, then convert the Datetime to Iso, before putting it in the format
      return dates.formatAsTime(dates.convertDatetimeToISO(dates.addUserTimezone(timestamp)));
    },
    loadFusTemplates() {
      console.log('Trying to reload');
      this.loading = true;

      return api.fusTemplates.all().then(response => {
        this.followUpTemplates = response.data;
      }).finally(() => this.loading = false);
    },
    createFollowUp() {
      this.$refs.createFollowUpModal.open();
    },

    // Can only be accessed if the updateFollowUpModal is open
    // So we know that the current follow up will be set
    createFollowUpStep() {
      this.$refs.followUpStepModal.open({
        'FUS_template_id': this.currentFollowUp.id,
      });
    },

    viewRecords(followUp) {
      this.currentFollowUpId = followUp.id;
      this.loadingRecordPreviews = true;
      this.recordPreviews = [];

      this.$refs.recordsModal.open(followUp);
      this.$api.records.getRecordsInFollowUpTemplate(followUp.id).then(response => {
        this.recordPreviews = response.data;
        this.loadingRecordPreviews = false;
      }).catch((error) => {
        this.recordPreviews = [];
        this.loadingRecordPreviews = false;

        throw error;
      })
    },

    editFollowUp(followUp) {
      this.currentFollowUpId = followUp.id;

      this.$refs.updateFollowUpModal.open(followUp);
      this.$api.fusStepTemplates.allForTemplate(followUp.id).then(response => {
        this.steps = response.data;
        this.loadingSteps = false;
      }).catch((error) => {
        this.steps = [];
        this.loadingSteps = false;

        throw error;
      })
    },
    openRecordModal(recordId) {
      router.replace({query: {lead: recordId}});
    },
    editFollowUpStep(step) {
      this.$refs.followUpStepModal.open(step);
    },
    duplicate(templateId) {
      this.isDuplicating = true;
      this.$api.fusTemplates.clone(templateId)
        .then(res => {
          this.$buefy.toast.open({
            message: "Copied Follow Up",
            type: 'is-success'
          });

          this.editFollowUp(res.data);
        })
        .finally(() => {
          this.isDuplicating = false
          this.loadFusTemplates();
        });
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.upgrade_message {
  max-width: 700px;
  margin: 0 auto;
  padding: 4rem 0 2rem;

  &--heading {
    margin-bottom: 1.3rem;
    font-size: 2rem;
    font-weight: bold;
    text-align: center;
  }

  &--content {
    margin-bottom: 10px;
    font-size: 1.125rem;
    line-height: 1.4;

    a {
      font-weight: bold;
    }
  }

  &--button {
    margin-top: 1.5rem;
  }
}
</style>
